// src/App.jsx
import { useState, useEffect, useCallback, useRef, lazy, Suspense, Component } from 'react'
import { createPortal } from 'react-dom'
import { useSchedule }      from './hooks/useSchedule.js'
import { useConfig }        from './hooks/useConfig.js'
import { usePhoneNumbers }  from './hooks/usePhoneNumbers.js'
import { useDeliberateBackdropDismiss } from './hooks/useDeliberateBackdropDismiss.js'
import { AgentsTab }        from './components/AgentsTab.jsx'
import { CandidatesTab }    from './components/CandidatesTab.jsx'
import { ListingsTab }      from './components/ListingsTab.jsx'
import { DealsTab }         from './components/DealsTab.jsx'
import IntakeTab, { DESK_BUTTONS } from './components/IntakeTab.jsx'
// Code-split (2026-07-17): these four tabs are OUTSIDE IntakeTab's import graph, so
// lazy-loading them genuinely shrinks the initial bundle — StatsTab alone carries
// recharts. The other tabs (Agents/Candidates/Listings/Deals) are already pulled in
// by IntakeTab's static imports (ViewToggle, CandidateModal, PLATFORM_LABEL,
// ViewingForm), so lazying them would split nothing — don't bother.
// Lazy import that survives a post-deploy chunk-hash change (2026-07-21: the Schedules/
// Attendance tab went BLANK until a manual refresh). When an already-open page requests a
// chunk hash Vercel no longer serves, import() rejects with a chunk-load error; with no error
// boundary React unmounts the whole tree → blank screen. Reload ONCE to pull the fresh
// index.html + hashes (a sessionStorage flag guards against an infinite loop if it's a real
// code error, not a stale chunk); the flag clears on a successful load so a LATER deploy in
// the same session can retry again. TabErrorBoundary below catches the already-retried case.
function lazyWithRetry(factory, key) {
  const flag = `sva-reload-lazy-${key}`
  return lazy(() => factory()
    .then(m => { try { sessionStorage.removeItem(flag) } catch { /* private mode */ } return m })
    .catch(err => {
      let retried = true
      try { retried = !!sessionStorage.getItem(flag); if (!retried) sessionStorage.setItem(flag, '1') } catch { /* private mode */ }
      if (!retried) { window.location.reload(); return new Promise(() => {}) }  // never resolves — page is reloading
      throw err   // already retried once → let the ErrorBoundary render a graceful message
    }))
}

const StatsTab      = lazyWithRetry(() => import('./components/StatsTab.jsx').then(m => ({ default: m.StatsTab })), 'stats')
const SettingsTab   = lazyWithRetry(() => import('./components/SettingsTab.jsx').then(m => ({ default: m.SettingsTab })), 'settings')
const AdminTab      = lazyWithRetry(() => import('./components/AdminTab.jsx').then(m => ({ default: m.AdminTab })), 'admin')
import { MyAttendance } from './components/MyAttendance.jsx'
import { ShiftPrompt } from './components/ShiftPrompt.jsx'
import { useOverrides } from './hooks/useOverrides.js'
import { todayStr } from './lib/clock.js'
const AttendanceTab = lazyWithRetry(() => import('./components/AttendanceTab.jsx').then(m => ({ default: m.AttendanceTab })), 'attendance')
const ScheduleTab   = lazyWithRetry(() => import('./components/ScheduleTab.jsx').then(m => ({ default: m.ScheduleTab })), 'schedule')
// The contract PAGE (owner 2026-09-10: "we need this to be a separate link … it must be
// linkable to send"). Not a tab: `#contract/<id>` and `#contract/new?meeting=…&template=…`
// render it in place of the tab body with the header and nav still up and nothing lit.
const ContractPage  = lazyWithRetry(() => import('./components/desk/ContractPage.jsx'), 'contract')
// The contracts TAB (owner 2026-09-11) — the whole book of drafts, for every role. Distinct
// from ContractPage above: that is one document at `#contract/<id>`, this is the list at
// `#contracts`. ⚠️ The two hashes differ by one letter and do NOT collide:
// getContractRouteFromHash's regex requires `/`, `?` or end right after "contract", so
// "contracts?q=…" is not a contract route — and getTabFromHash cuts at the first `?`, so it
// is the tab. Keep both halves true if either regex is ever touched.
const ContractsTab  = lazyWithRetry(() => import('./components/ContractsTab.jsx').then(m => ({ default: m.ContractsTab })), 'contracts')

// `#contract/…` → the hash itself (the page parses it), anything else → null. Read on mount
// and on hashchange, so a reload lands on the same contract — which is what makes it a link.
function getContractRouteFromHash() {
  const h = window.location.hash.slice(1)
  return /^contract(\/|\?|$)/.test(h) ? window.location.hash : null
}

// Error boundary so a failed tab render (e.g. a lazy chunk that failed even after the reload
// retry above, or any runtime error in a tab) shows a Reload prompt instead of a blank screen.
// Keyed by tab in the render below, so switching tabs remounts it and clears a prior error.
class TabErrorBoundary extends Component {
  state = { error: null }
  static getDerivedStateFromError(error) { return { error } }
  // Report it, do not just print it (2026-09-08). This screen has been reported by agents
  // for weeks as an intermittent "failed to load" and was undiagnosable because the only
  // record was a console line in their browser. See logClientError.
  // ⚠️ `tabId` is a PROP as well as the `key` below: a key remounts the boundary on a tab
  // change but is not readable from inside the component, and a crash report that cannot
  // say which tab died is most of the way to useless.
  componentDidCatch(error, info) {
    console.error('[tab-error-boundary]', error, info?.componentStack)
    logClientError('tab:' + (this.props.tabId || '?'), error, info)
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center py-24 text-sm text-muted-foreground gap-3">
          <span>{t('app_tab_load_failed')}</span>
          <button onClick={() => window.location.reload()} className="underline">{t('app_error_retry')}</button>
        </div>
      )
    }
    return this.props.children
  }
}

// Warns when the device clock disagrees with the server by more than a few minutes — the exact
// condition that used to write wrong attendance times. Schedule math already ignores the device
// clock (lib/clock.js), so this is a heads-up, not a blocker. Re-renders when the sync lands.
function ClockSkewBanner() {
  useClock(0)
  if (!clockSynced()) return null
  const skewMin = Math.round(clockSkewMs() / 60000)
  if (skewMin < 3) return null
  return (
    <div className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
      ⏰ {t('clock_skew_warning').replace('{mins}', String(skewMin))}
    </div>
  )
}
const TabFallback = () => <div className="py-24 text-center text-sm text-muted-foreground">…</div>
import { MODE, getMyRoleInfo, getMyRoleHistory } from './db/index.js'
import { getUsdGelRate } from './db/core.js'
import { setCurrentAdmin, getCurrentAdmin, setLogActor, logClientError } from './lib/agentLog.js'
import { getLanguage, setLanguage, t } from './lib/i18n.js'
import { syncClock, clockSkewMs, clockSynced, useClock } from './lib/clock.js'
import { useAuth, authEnabled, userDisplayName } from './hooks/useAuth.js'
import { LoginScreen }       from './components/LoginScreen.jsx'
import { UserMenu }          from './components/UserMenu.jsx'
import { ExtensionUpdateBanner, ExtensionHeaderButton } from './components/ExtensionUpdate.jsx'
import { ChatInboxButton }   from './components/desk/ChatInbox.jsx'
import { can, settingsSubTabs } from './lib/permissions.js'
import { Icon }              from './components/Icon.jsx'
import { keepInView } from './lib/keepInView.js'

// Tabs — icon = Icon.jsx registry name (monochrome SVG, 2026-07-17 emoji→icon sweep).
const TABS = [
  { id: 'datadesk',   label: 'Desk',               icon: 'layout-dashboard' },
  { id: 'attendance', labelKey: 'nav_attendance',  icon: 'clock' },
  // Directly after attendance (owner 2026-08-03): the two answer the same question one
  // step apart — who was working, and what they did while they were. Its id stays
  // 'listings' (routes, permissions and saved links all key on it) while the LABEL is now
  // აქტივობა, because the page long ago stopped being only about listings: it carries
  // meetings, shifts and the day's whole shape.
  { id: 'listings',   labelKey: 'nav_listings',    icon: 'chart' },
  { id: 'candidates', labelKey: 'nav_candidates',  icon: 'briefcase' },
  { id: 'agents',     labelKey: 'nav_agents',      icon: 'user' },
  { id: 'deals',      labelKey: 'nav_deals',       icon: 'handshake' },
  // Directly after გარიგებები (owner 2026-09-11, moving it from beside the Desk): a contract
  // is what a deal BECOMES, so it belongs next to the deals it comes out of rather than next
  // to the search the work starts in. Visible to every role; see `tab.contracts` in
  // permissions.js. ⚠️ Order here is the only thing that changed — the id, the route and the
  // permission are untouched, so existing #contracts links keep working.
  { id: 'contracts',  labelKey: 'nav_contracts',   icon: 'file-text' },
  { id: 'stats',      labelKey: 'nav_stats',       icon: 'chart' },
  // Manager → Settings (owner 2026-09-16): Numbers + Office Configuration, office-scoped. The same
  // components Admin uses; see SettingsTab.jsx and `tab.settings` in permissions.js.
  { id: 'settings',   labelKey: 'nav_settings',    icon: 'settings' },
  { id: 'admin',      labelKey: 'nav_admin',       icon: 'settings' },
]

// ─── Admin name modal ─────────────────────────────────────────────────────────
function AdminModal({ onClose }) {
  const backdropDismiss = useDeliberateBackdropDismiss(onClose)
  const [name, setName] = useState(getCurrentAdmin())
  function handleSave() { setCurrentAdmin(name.trim() || 'Admin'); onClose() }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      {...backdropDismiss}>
      <div className="bg-card border border-border rounded-xl w-full max-w-xs shadow-xl">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-base font-semibold">{t('admin_name')}</h2>
          <p className="text-xs text-muted-foreground mt-1">{t('admin_note')}</p>
        </div>
        <div className="px-5 py-4">
          <input value={name} onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()} autoFocus
            className="w-full rounded-lg border border-input px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-border">
          <button onClick={onClose} className="text-sm font-medium px-4 py-2 rounded-lg border border-border hover:bg-accent">{t('cancel')}</button>
          <button onClick={handleSave} className="text-sm font-medium px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90">{t('save')}</button>
        </div>
      </div>
    </div>
  )
}

// ─── Language toggle ──────────────────────────────────────────────────────────
function LangToggle({ lang, onChange }) {
  return (
    <button
      onClick={() => onChange(lang === 'en' ? 'ka' : 'en')}
      title={lang === 'en' ? t('app_switch_to_georgian') : t('app_switch_to_english')}
      className="text-xs font-semibold px-2 py-1 rounded-lg border border-border hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
    >
      {lang === 'en' ? 'ქარ' : 'ENG'}
    </button>
  )
}

// ─── Currency toggle (Desk advanced-search filter) ────────────────────────────
// Same click-to-switch shape as LangToggle — a picker inside the search panel felt like a
// dropdown; this cycles USD ⇄ GEL with one click, no menu.
function CurrencyToggle({ currency, onChange }) {
  return (
    <button
      onClick={() => onChange(currency === 'USD' ? 'GEL' : 'USD')}
      title="Switch search currency"
      className="text-xs font-semibold px-2 py-1 rounded-lg border border-border hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
    >
      {currency === 'USD' ? '$' : '₾'}
    </button>
  )
}

// ─── Daily USD→GEL rate badge (top bar, next to the currency toggle) ──────────
// Official NBG daily rate via our server (one upstream fetch/day, last-good
// persisted there). Amber = stale: today's refresh failed and this is the
// previous day's number — agents should KNOW they're seeing an old rate, not
// silently trust it. localStorage keeps the last seen value for the case where
// even our server is unreachable.
// The rate itself lives in App (2026-07-20): once the currency toggle started
// CONVERTING prices rather than just filtering by them, the Desk needs the same
// number this badge shows. One fetch, one rate — a card reading "≈2 894 ₾" and a
// badge reading a different rate would be indefensible.
function useUsdGelRate() {
  const [fx, setFx] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sva_fx_usdgel')) } catch { return null }
  })
  useEffect(() => {
    let dead = false
    getUsdGelRate()
      .then(d => { if (dead) return; setFx(d); try { localStorage.setItem('sva_fx_usdgel', JSON.stringify(d)) } catch { /* quota */ } })
      .catch(() => { if (!dead) setFx(prev => prev ? { ...prev, stale: true } : null) })
    return () => { dead = true }
  }, [])
  return fx
}

// ─── Mobile prefs menu (owner 2026-07-24) ─────────────────────────────────────
// On <sm the header can't fit currency + fx rate + language + theme as separate
// buttons once admin also has office/view-as/bell/plus — ONE ⚙️ collapses them.
// Office + view-as live here too since 2026-09-28 (owner: the bar overflowed a phone, the ⚙
// itself was pushed off-screen). `pickers` = [{ key, label, value, options, onChange, active }].
// ⚠️ A non-default pick must stay visible from the CLOSED menu — a quietly-stuck office filter
// made every tab look mysteriously empty (2026-07-13 audit) — hence the amber ⚙ + dot.
// Rows TOGGLE in place and the menu stays open (you often flip two at once);
// outside-click/Escape closes. Desktop keeps the individual buttons.
function MobilePrefsMenu({ currency, onCurrency, lang, onLang, isDark, onDark, fx, pickers = [] }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [open])
  const Row = ({ label, value, onClick }) => (
    <button type="button" onClick={onClick}
      className="w-full text-left px-3 py-2.5 text-xs flex items-center justify-between gap-3 hover:bg-accent transition-colors">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold">{value}</span>
    </button>
  )
  const anyActive = pickers.some(p => p.active)
  return (
    <div className="relative sm:hidden" ref={ref}>
      <button type="button" title={t('prefs_menu_title')} onClick={() => setOpen(o => !o)}
        className={`relative w-9 h-9 flex items-center justify-center rounded-lg border text-base text-muted-foreground transition-colors ${anyActive ? 'border-amber-500' : 'border-border hover:border-foreground/30'}`}>
        ⚙️
        {anyActive && <span aria-hidden className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-amber-500" />}
      </button>
      {open && (
        <div ref={keepInView} className="absolute right-0 mt-1 w-60 rounded-lg border border-border bg-card shadow-lg py-1 z-30">
          {pickers.map(p => (
            <label key={p.key} className="px-3 py-2 text-xs flex items-center justify-between gap-3">
              <span className="text-muted-foreground shrink-0">{p.label}</span>
              <select value={p.value} onChange={e => p.onChange(e.target.value)}
                className={`min-w-0 max-w-[9.5rem] truncate rounded-md border px-1.5 py-1 text-xs bg-background ${p.active ? 'border-amber-500 text-amber-600 dark:text-amber-400 font-semibold' : 'border-border text-foreground'}`}>
                {p.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          ))}
          {pickers.length > 0 && <div className="border-t border-border my-1" />}
          <Row label={t('prefs_currency')} value={currency === 'USD' ? '$ USD' : '₾ GEL'}
            onClick={() => onCurrency(currency === 'USD' ? 'GEL' : 'USD')} />
          <Row label={t('prefs_language')} value={lang === 'en' ? 'ENG' : 'ქარ'}
            onClick={() => onLang(lang === 'en' ? 'ka' : 'en')} />
          <Row label={t('prefs_theme')} value={isDark ? '🌙' : '☀️'} onClick={onDark} />
          {fx?.rate && (
            <div className={`px-3 py-1.5 text-xs border-t border-border mt-1 tabular-nums ${fx.stale ? 'text-amber-500' : 'text-muted-foreground'}`}>
              1 USD = {Number(fx.rate).toFixed(2)} ₾{fx.stale ? ' ⚠' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Clicking the badge opens a converter at THAT rate — the same number the badge
// shows and the Desk converts with, so an agent quoting a client can never be
// working off a different rate than the cards. Both sides are editable (typing in
// one computes the other); ⇅ swaps which currency sits on top and carries the
// typed amount across, so "how much is 2 500 ₾" needs no mental re-mapping.
const FX_MENU_W = 240
function UsdGelRate({ fx }) {
  const [pos, setPos] = useState(null)     // fixed-viewport coords for the portal; null = closed
  const [side, setSide] = useState('USD')  // which currency is on TOP (and is the one typed in)
  const [amount, setAmount] = useState('')
  const btnRef = useRef(null)
  const panelRef = useRef(null)

  useEffect(() => {
    if (!pos) return
    function onDoc(e) {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return
      setPos(null)
    }
    function onKey(e) { if (e.key === 'Escape') setPos(null) }
    const close = () => setPos(null)
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', close)
    }
  }, [pos])

  if (!fx?.rate) return null
  const rate = Number(fx.rate)

  const n = Number(String(amount).replace(/[\s,]/g, ''))
  const valid = amount.trim() !== '' && Number.isFinite(n)
  // Converted value only — the typed side keeps the raw string so typing "1." works.
  const converted = !valid ? '' : (side === 'USD' ? n * rate : n / rate)
    .toLocaleString('en-US', { maximumFractionDigits: 2 })

  function swap() {
    // Swap places: the number you were looking at becomes the one you're typing.
    setAmount(valid ? String(Number((side === 'USD' ? n * rate : n / rate).toFixed(2))) : '')
    setSide(s => (s === 'USD' ? 'GEL' : 'USD'))
  }

  const field = (cur, value, editable) => (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-2 py-1.5">
      <span className="text-xs font-semibold text-muted-foreground w-6 text-center">{cur === 'USD' ? '$' : '₾'}</span>
      <input
        value={value}
        readOnly={!editable}
        onChange={editable ? e => setAmount(e.target.value) : undefined}
        inputMode="decimal"
        placeholder="0"
        autoFocus={editable}
        className={`w-full bg-transparent text-sm tabular-nums text-right focus:outline-none ${editable ? '' : 'text-muted-foreground'}`}
      />
      <span className="text-[10px] text-muted-foreground w-7">{cur}</span>
    </div>
  )

  return (
    <div className="relative" ref={btnRef}>
      <button
        type="button"
        onClick={() => setPos(p => {
          if (p) return null
          const r = btnRef.current.getBoundingClientRect()
          return {
            top: r.bottom + 4,
            left: Math.max(8, Math.min(r.right - FX_MENU_W, window.innerWidth - FX_MENU_W - 8)),
          }
        })}
        title={`1 USD = ${fx.rate} GEL (NBG, ${fx.rate_date})${fx.stale ? ` — ${t('fx_stale_tooltip')}` : ''}`}
        className={`text-xs font-semibold px-2 py-1 rounded-lg border tabular-nums transition-colors hover:bg-accent ${fx.stale
          ? 'border-amber-500/50 text-amber-500'
          : 'border-border text-muted-foreground hover:text-foreground'}`}
      >
        {rate.toFixed(2)}
      </button>
      {pos && createPortal(
        <div ref={panelRef} style={{ ...pos, width: FX_MENU_W }}
          className="fixed rounded-lg border border-border bg-card shadow-lg p-3 z-[200]">
          <div className={`text-xs mb-2 tabular-nums ${fx.stale ? 'text-amber-500' : 'text-muted-foreground'}`}>
            1 USD = {rate.toFixed(4)} ₾ · NBG {fx.rate_date}{fx.stale ? ' ⚠' : ''}
          </div>
          {field(side, amount, true)}
          <div className="flex justify-center py-1">
            <button type="button" onClick={swap} title="Swap"
              className="w-7 h-7 flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
              ⇅
            </button>
          </div>
          {field(side === 'USD' ? 'GEL' : 'USD', converted, false)}
        </div>,
        document.body
      )}
    </div>
  )
}

// ─── App ──────────────────────────────────────────────────────────────────────
// ─── Dark mode ────────────────────────────────────────────────────────────────
function initDark() {
  const saved = localStorage.getItem('sva_theme')
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  if (saved === 'dark' || (!saved && prefersDark)) {
    document.documentElement.classList.add('dark')
    return true
  }
  return false
}

function toggleDark() {
  const isDark = document.documentElement.classList.toggle('dark')
  localStorage.setItem('sva_theme', isDark ? 'dark' : 'light')
  return isDark
}

function getTabFromHash() {
  // The tab is everything before the first '/' or '?' — '#datadesk?q=…' (committed-search
  // URLs, deskUrl.js) and '#datadesk/listing/<id>' both resolve to tab 'datadesk'.
  const hash = window.location.hash.slice(1).split(/[/?]/)[0]
  const valid = TABS.map(t => t.id)
  // Default landing = Desk (2026-07-17, user-directed; was 'attendance'). Roles without
  // tab.datadesk are bounced to their first visible tab by the effect below anyway.
  return valid.includes(hash) ? hash : 'datadesk'
}

// Auth gate: show the login screen until there's a session (skipped in local mode).
export default function App() {
  const session = useAuth()
  if (authEnabled && session === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">
        <svg className="animate-spin w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
        </svg>
        Loading…
      </div>
    )
  }
  // 🔗 Someone followed the client page's logo here carrying a share token, and
  // there is no agent session on this origin. That is the ordinary case for the
  // CLIENT — the link was written for them — so send them back to the page they
  // came from instead of showing them a login screen they can never satisfy.
  //
  // ⚠️ This is the reason the logo check cannot live on the share page itself.
  // That page is served from api.sentavus.com; the Desk runs on this origin and
  // the Supabase session lives in THIS origin's storage. Cross-origin there is no
  // token to read, so the "is an agent logged in?" question can only be answered
  // here. The redirect is the fallback half of that answer.
  //
  // Only reachable after the loading branch above, so !session here means the
  // session is genuinely absent, not merely unresolved.
  if (authEnabled && !session) {
    const m = (window.location.hash || '').match(/^#datadesk\/share\/([a-f0-9]{32})$/i)
    if (m) { window.location.replace(`https://api.sentavus.com/s/${m[1]}`); return null }
    return <LoginScreen />
  }
  return <Dashboard session={session} />
}

function Dashboard({ session }) {
  const [isDark,      setIsDark]      = useState(() => initDark())
  const [tab,         setTab]         = useState(getTabFromHash)
  // The contract page's route, when the hash names one (null otherwise). It sits BESIDE the
  // tab rather than in it: `tab` keeps its last value underneath, so leaving the page (any
  // nav click, the logo, Back) lands where the agent was. The ref marks a mount that ARRIVED
  // on a contract link, so the tab→hash sync below does not overwrite the link with
  // `#datadesk` before the page has read it (the same clobber IntakeTab's URL writer had to
  // learn about).
  const [contractRoute, setContractRoute] = useState(getContractRouteFromHash)
  const contractMountRef = useRef(!!getContractRouteFromHash())
  // Desk deep links (#datadesk/listing/<id>) and committed-search URLs (#datadesk?q=…)
  // are owned entirely by IntakeTab now (ONE URL mechanism, deskUrl.parseHash — plan
  // §2.6): it reads the hash itself on mount and on hashchange. App only routes the TAB
  // segment, and the sync effect below never clobbers a same-tab payload.
  // Header-logo → Desk-home clicks (see the logo button): a counter, not a boolean, so
  // repeat clicks while already on the Desk still re-trigger the reset effect.
  const [deskHomeSignal, setDeskHomeSignal] = useState(0)
  // Mobile header bell (design ruling 2026-07-24 §5): interrupts get a FIXED position — the
  // Desk strip's pill row is fine on desktop, but on mobile a badge you might scroll past
  // is not a badge. Same signal-counter pattern as deskHomeSignal: tapping lands on the
  // Desk and pops the NotificationsPanel; IntakeTab reports its badge count up so the bell
  // is truthful even while another tab is open (count freezes at last known until remount).
  const [notifSignal, setNotifSignal] = useState(0)
  const [notifBadge, setNotifBadge] = useState(0)
  // ── MY SHELVES (owner ruling 2026-08-09) ────────────────────────────────────────────
  // ♥ favourites and 🗄 hidden used to live in the Desk's search panel, and the owner's
  // objection was exactly right: *"I guess it's something different than a filter."* Every
  // other control in that panel asks a question about the INVENTORY — its status, its city,
  // its district, its price. These two are facts about YOU; nothing in the corpus changes when
  // you favourite something. That mismatch is what made every axis go inert the moment one was
  // clicked (B625) — the panel had no inventory question left to answer.
  // So they move to the header, where per-agent state already lives, on exactly the bell's
  // pattern: a counter signal (repeat presses re-fire) plus a badge IntakeTab reports upward,
  // so the header stays truthful while another tab is open.
  const [shelfSignal, setShelfSignal] = useState(null)   // {n, shelf:'Favorites'|'Archived'}
  const [favBadge, setFavBadge] = useState(0)
  const [hiddenBadge, setHiddenBadge] = useState(0)
  // Mobile header "+" (owner 2026-07-24): quick-add client/property/business from anywhere.
  // {n, kind} — n makes repeat picks of the same kind re-fire the effect in IntakeTab.
  const [addMenuOpen, setAddMenuOpen] = useState(false)
  const [addSignal, setAddSignal] = useState(null)
  const [adminModal,  setAdminModal]  = useState(false)
  const [adminName,   setAdminName]   = useState(() => getCurrentAdmin())
  const [lang,        setLang]        = useState(() => getLanguage())
  const [currency,    setCurrency]    = useState('USD')   // Desk display + search currency
  const fx = useUsdGelRate()                              // NBG daily USD→GEL, shared with the Desk
  const [officeFilter, setOfficeFilter] = useState('all')
  const [role,        setRole]        = useState(null)   // real role from DB: 'admin' | 'general_manager' | 'manager' | 'agent' | 'agent_hr'
  const [roleAssignedAt, setRoleAssignedAt] = useState(null) // when `role` was last set — see AgentsTab
  const [roleHistory, setRoleHistory] = useState([])     // every past role period — see AgentsTab
  const [viewAs,      setViewAs]      = useState(null)   // admin-only: preview the app as another role

  useEffect(() => {
    getMyRoleInfo().then(({ role, roleAssignedAt }) => { setRole(role); setRoleAssignedAt(roleAssignedAt) })
    getMyRoleHistory().then(setRoleHistory)
  }, [])

  const isAdmin       = role === 'admin'
  const effectiveRole = (isAdmin && viewAs) || role      // only a real admin may impersonate
  // The attendance tab has TWO doors behind one label (owner 2026-09-02): management gets
  // the roster grid, an agent gets their own card. 'tab.attendance.self' is what puts the
  // label in front of an agent; which COMPONENT renders is decided at the render site, so
  // the tab list stays a list of labels rather than a second place the rule lives.
  // Settings is shown to every role now (Notifications, 0017). SettingsTab itself drops the Numbers /
  // Office sub-tabs for a role that already reaches them through Admin.
  const visibleTabs   = TABS.filter(tab_ => tab_.id === 'settings'
    // Settings only when it has something the Admin panel does not already give this role.
    ? can(effectiveRole, 'tab.settings') && settingsSubTabs(effectiveRole).length > 0
    : can(effectiveRole, 'tab.' + tab_.id)
      || (tab_.id === 'attendance' && can(effectiveRole, 'tab.attendance.self')))
  const tabVisible    = visibleTabs.some(tab_ => tab_.id === tab)

  // Bounce the active tab to the first allowed one when the effective role changes
  // (e.g. admin previews 'agent', or lands on a tab their role can't see).
  useEffect(() => {
    if (role !== null && visibleTabs.length && !tabVisible) setTab(visibleTabs[0].id)
  }, [effectiveRole, role])   // eslint-disable-line react-hooks/exhaustive-deps

  const {
    agents, offices, loading, error, reload,
    createAgent, editAgent, removeAgent, editOffice, createOffice, removeOffice,
  } = useSchedule()

  const { config, updateKey } = useConfig()
  const { phones, historyByAgent: phoneHistory, add: addPhone, remove: removePhone, update: updatePhone, reload: reloadPhones } = usePhoneNumbers()

  // Office picker (header): admin/general_manager may browse any office or "all". manager/
  // agent_hr are locked to their OWN assigned office — no picker, no way to self-select a
  // different one. 'unassigned' fallback (not 'all') if their own agent record has no office
  // yet, so a not-yet-set-up manager never defaults to seeing everyone (fail closed, same
  // principle as the core-search scoping elsewhere in this app).
  const myAgentRecord  = agents.find(a => a.auth_user_id && a.auth_user_id === session?.user?.id)
  const myOfficeId     = myAgentRecord?.office_id || null

  // ── T31: the shift-start prompt (owner 2026-09-02) ────────────────────────────────────────
  // A one-minute tick, because the banner has to appear when the CLOCK crosses the shift start
  // on a page nobody has touched. Local time only — it reads no data and asks the database
  // nothing, so it costs a render per minute and not a query.
  const [shiftTick, setShiftTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setShiftTick(v => v + 1), 60_000)
    return () => clearInterval(id)
  }, [])
  // Today's one-off schedule changes. Loaded for the whole roster because that is the shape
  // useOverrides has and one day of rows is small; the prompt reads only its own agent's entry.
  // ⚠️ A moved or cancelled shift lives HERE, not in agent.schedule — without this a manager
  // could give someone the morning off and the app would still tell them they were late.
  const shiftDate = todayStr()
  const { byAgent: todayOverrides } = useOverrides([], shiftDate, shiftDate)
  // Every audit-log write from here on carries WHO acted and from WHICH OFFICE, so recruiting
  // and roster activity can be attributed to a person instead of to a typed label (0006).
  useEffect(() => { setLogActor(myAgentRecord || null) }, [myAgentRecord?.id, myAgentRecord?.office_id])
  const canPickOffice  = effectiveRole === 'admin' || effectiveRole === 'general_manager'
  useEffect(() => {
    // role loads async and starts null — canPickOffice would be false for that instant,
    // which without this guard locked even a real admin to 'unassigned' before their role
    // resolved, and (since this effect never restores 'all') it never recovered.
    if (role === null) return
    if (!canPickOffice) setOfficeFilter(myOfficeId || 'unassigned')
  }, [role, canPickOffice, myOfficeId])

  const createAgentAndPhones = useCallback(async (data) => {
    const agent = await createAgent(data)
    if (data.phone_work) reloadPhones()
    return agent
  }, [createAgent, reloadPhones])

  const editAgentAndPhones = useCallback(async (id, updates) => {
    const agent = await editAgent(id, updates)
    if (updates.phone_work !== undefined) reloadPhones()
    return agent
  }, [editAgent, reloadPhones])


  function handleChangeAdmin(name) {
    setCurrentAdmin(name)
    setAdminName(name)
  }

  // Sync tab → hash — ONLY when the hash's tab segment actually differs. Writing
  // unconditionally used to strip the deep-link payload on mount; now that IntakeTab
  // stores its committed search in the hash ('#datadesk?q=…'), an unconditional write
  // would also clobber that on every render of this effect. Same-tab payloads are
  // IntakeTab's property; App touches the hash only to change tabs.
  useEffect(() => {
    const cur = window.location.hash.slice(1).split(/[/?]/)[0]
    if (cur === tab) return
    // Mounted on `#contract/…`: the tab is only the default underneath the page. Leave the
    // link alone this once; any LATER tab change is the agent leaving the page and must write.
    if (cur === 'contract' && contractMountRef.current) { contractMountRef.current = false; return }
    window.location.hash = tab
  }, [tab])

  // Handle browser back/forward and pasted links: re-resolve the TAB segment. The
  // payload after it (search params / listing deep link) is parsed by IntakeTab's own
  // hashchange listener via deskUrl.parseHash — one URL mechanism, not two (plan §2.6).
  // The contract page rides the same event: `#contract/…` sets its route, anything else
  // clears it. (getTabFromHash maps `contract` to the default tab; the route wins the body.)
  useEffect(() => {
    const handler = () => {
      const cr = getContractRouteFromHash()
      setContractRoute(cr)
      if (!cr) setTab(getTabFromHash())
    }
    window.addEventListener('hashchange', handler)
    return () => window.removeEventListener('hashchange', handler)
  }, [])
  // Leaving the page by a nav click. `setTab` alone is not enough when the tab underneath is
  // already the one pressed (the effect above then has nothing to write), so the hash is
  // written here too; hashchange clears the route.
  const leaveContractTo = useCallback(id => {
    setTab(id)
    if (getContractRouteFromHash()) window.location.hash = id
  }, [])

  // Probe the server clock once at startup so attendance/schedule run on Tbilisi time even when
  // the device clock is wrong or in another timezone (2026-07-21). Best-effort; falls back to
  // device time if the probe fails. See lib/clock.js.
  useEffect(() => { syncClock() }, [])

  // With auth on, the admin identity comes from the logged-in user; otherwise
  // (local mode) fall back to the manual name prompt on first run.
  useEffect(() => {
    if (authEnabled) {
      const nm = userDisplayName(session)
      if (nm) { setCurrentAdmin(nm); setAdminName(nm) }
    } else if (!localStorage.getItem('sva_current_admin')) {
      setAdminModal(true)
    }
  }, [session])

  function handleAdminClose() { setAdminName(getCurrentAdmin()); setAdminModal(false) }

  function handleLangChange(newLang) {
    setLanguage(newLang)
    setLang(newLang)
  }

  const [newlyHiredAgent, setNewlyHiredAgent] = useState(null)
  const [revertCandidateId, setRevertCandidateId] = useState(null)
  // Add-candidate, requested from three places (the Desk dropdown, the phone's "+", and any
  // future caller) and answered in ONE: App is the only component that can change tabs, so
  // the other two ask by dispatching 'sentavus:add-candidate' and this is what happens next.
  // The signal is a counter rather than a boolean because pressing + twice must re-open the
  // form the second time; CandidatesTab clears it once it has acted (onAddHandled), which is
  // what stops a later visit to the tab from re-opening a modal nobody asked for.
  const [candidateAddSignal, setCandidateAddSignal] = useState(0)
  const openAddCandidate = useCallback(() => {
    setTab('candidates')
    setCandidateAddSignal(n => n + 1)
  }, [])
  useEffect(() => {
    window.addEventListener('sentavus:add-candidate', openAddCandidate)
    return () => window.removeEventListener('sentavus:add-candidate', openAddCandidate)
  }, [openAddCandidate])
  // …and the same door for ONE existing candidate, which the Desk's new-applicant banner
  // uses. Separate from revertCandidateId on purpose: that one means "a hire was undone",
  // this means "someone applied and nobody has called them" — one state serving both would
  // make the reason unreadable at the point where the modal decides what to say.
  const [openCandidateId, setOpenCandidateId] = useState(null)
  useEffect(() => {
    const handler = e => { setTab('candidates'); setOpenCandidateId(e?.detail?.id || null) }
    window.addEventListener('sentavus:open-candidate', handler)
    return () => window.removeEventListener('sentavus:open-candidate', handler)
  }, [])

  const filteredAgents = agents.filter(a => {
    if (officeFilter === 'all') return true
    if (officeFilter === 'unassigned') return !a.office_id
    return a.office_id === officeFilter
  })

  const handleAgentCreated = useCallback((agent) => {
    reload()
    setTab('agents')
    if (agent?.id) setNewlyHiredAgent({ ...agent, _revertHire: true })
  }, [reload])

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* Top bar */}
      {/* Opaque, NO backdrop-blur (2026-07-05): the header's own backdrop-filter made Chromium
          composite it independently of modal overlays (also backdrop-filtered), so it painted
          bright/crisp ABOVE the dimmed modal backdrop despite z-20 < z-50. Solid bg avoids the
          nested-backdrop-filter quirk entirely. */}
      <header className="border-b border-border bg-card sticky top-0 z-20">
        <div className="w-full px-4 h-14 flex items-center gap-3">
          {/* Logo = home button (eBay model, 2026-07-18 user ruling): from any tab it
              lands on the Desk cockpit. Every role has tab.datadesk, so no gate needed;
              the signal counter tells an already-mounted IntakeTab to reset (goDeskHome) —
              from other tabs IntakeTab mounts fresh, which is already the cockpit. */}
          <button type="button" aria-label="senTavus — home"
            className="flex items-center gap-2.5 shrink-0 pl-3 sm:pl-4 hover:opacity-80 transition-opacity"
            onClick={() => { leaveContractTo('datadesk'); setDeskHomeSignal(s => s + 1) }}>
            <img src="/logo.svg" alt="senTavus" className="h-7 w-auto" />
            {/* Brand capitalisation is deliberate (owner, 2026-07-30): senTavus, capital T.
                Not a typo — do not "correct" it to a leading capital S. */}
            <span className="text-muted-foreground text-sm hidden sm:inline">senTavus</span>
          </button>

          <div className="flex items-center gap-2 ml-auto">
            {offices.length > 0 && canPickOffice && (<>
              {/* Phone: this picker lives in the ⚙ menu (MobilePrefsMenu), not the bar.
                  manager/agent_hr never render it at all — they're locked to their own
                  office by the effect above, with no picker to override it. */}
              {/* Loud when non-default: a quietly-stuck office filter made every tab look
                  mysteriously empty (2026-07-13 UI audit) — an active filter must be visible
                  from across the room, not a gray select among grays. */}
              <select value={officeFilter} onChange={e => setOfficeFilter(e.target.value)} aria-label={t('office_filter_label')}
                className={`hidden sm:block text-xs border rounded-lg px-2 py-1 transition-colors ${officeFilter !== 'all'
                  ? 'border-primary bg-primary/15 text-primary font-semibold'
                  : 'border-border bg-background text-foreground hover:border-foreground/30'}`}>
                <option value="all">🏢 {t('office_filter_all')}</option>
                {offices.map(o => <option key={o.id} value={o.id}>🏢 {o.name}</option>)}
                <option value="unassigned">{t('office_filter_unassigned')}</option>
              </select>
            </>)}
            {isAdmin && (<>
              {/* Phone: in the ⚙ menu, like the office picker above. */}
              <select value={viewAs || 'admin'} onChange={e => setViewAs(e.target.value === 'admin' ? null : e.target.value)}
                title="Preview the app as a role"
                className={`hidden sm:block text-xs border rounded-lg px-2 py-1 bg-background transition-colors ${viewAs ? 'border-amber-500 text-amber-600 dark:text-amber-400' : 'border-border text-muted-foreground hover:border-foreground/30'}`}>
                <option value="admin">👁 Admin</option>
                <option value="general_manager">👁 General Manager</option>
                <option value="manager">👁 Manager</option>
                <option value="agent">👁 Agent</option>
                <option value="agent_hr">👁 Agent +HR</option>
              </select>
            </>)}
            {/* Mobile-only "+" — quick-add from anywhere (owner 2026-07-24). Same signal
                pattern as the bell; the menu ids are IntakeTab's DESK_BUTTONS ids. */}
            <div className="sm:hidden relative">
              <button type="button" aria-label={t('desk_strip_add')}
                className="w-11 h-11 -my-1 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                onClick={() => setAddMenuOpen(o => !o)}>
                <Icon name="plus" size={18} />
              </button>
              {addMenuOpen && (<>
                <button type="button" aria-label="close" className="fixed inset-0 z-40 cursor-default"
                  onClick={() => setAddMenuOpen(false)} />
                <div ref={keepInView} className="absolute right-0 top-full mt-1 z-50 rounded-lg border border-border bg-background shadow-lg py-1 w-48">
                  {/* THE SAME LIST the desktop dropdown renders (IntakeTab DESK_BUTTONS).
                      This used to be a hardcoded copy of the three ids, so 'meeting' shipped
                      to desktop and was simply absent on a phone. */}
                  {/* Capability filter, same as the desktop dropdown: 'candidate' carries
                      cap 'tab.candidates', so an agent's phone menu does not show it either
                      (owner 2026-09-09 — the phone was explicitly named). */}
                  {DESK_BUTTONS.filter(b => !b.cap || can(effectiveRole, b.cap)).map(b => (
                    <button key={b.id} type="button"
                      className="w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 hover:bg-accent transition-colors"
                      onClick={() => {
                        setAddMenuOpen(false)
                        // Not a Desk object: it has its own tab and its own create path.
                        if (b.id === 'candidate') { openAddCandidate(); return }
                        leaveContractTo('datadesk'); setAddSignal(s => ({ n: (s?.n || 0) + 1, kind: b.id }))
                      }}>
                      <Icon name={b.icon} size={15} /> {t(b.labelKey)}
                    </button>
                  ))}
                </div>
              </>)}
            </div>
            {/* ⬇ the extension download (owner 2026-09-29) — desktop only, see ExtensionUpdate.jsx. */}
            {!loading && role !== null && <ExtensionHeaderButton />}
            {/* ♥ my shortlist — same class of control as the bell beside it: personal,
                per-agent, small. It is a DESTINATION (a place you go, like a cart), which is a
                perfectly good shape — it was only wrong to file it under a filter panel.
                Always rendered, including at zero: unlike 🗄 below, an agent has to be able to
                find the shelf in order to learn it exists. The count badge is muted, not red —
                a shortlist is not an interrupt. */}
            <button type="button" aria-label={t('desk_type_favorites')} title={t('desk_type_favorites')}
              className="relative w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              onClick={() => { setTab('datadesk'); setShelfSignal(s => ({ n: (s?.n || 0) + 1, shelf: 'Favorites' })) }}>
              <span className="text-[15px] leading-none">♥</span>
              {favBadge > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-muted text-foreground text-[10px] leading-4 px-1 text-center tabular-nums font-semibold">{favBadge}</span>
              )}
            </button>
            {/* 🗄 hidden — rendered ONLY when something is hidden. In the old panel it was
                permanent even at zero, justified as "the only way back from an accidental
                archive"; that is a workaround for being in the wrong place, not a reason. The
                feed now says so itself where the hiding actually happens (see the inline
                "N hidden here" line in IntakeTab), so this is just the shelf. */}
            {hiddenBadge > 0 && (
              <button type="button" aria-label={t('desk_archive_view')} title={t('desk_archive_view')}
                className="relative w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                onClick={() => { setTab('datadesk'); setShelfSignal(s => ({ n: (s?.n || 0) + 1, shelf: 'Archived' })) }}>
                <Icon name="eye-off" size={16} />
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-muted text-foreground text-[10px] leading-4 px-1 text-center tabular-nums font-semibold">{hiddenBadge}</span>
              </button>
            )}
            {/* Messages (owner 2026-09-23, core 2240): website visitors' chats. Its own badge and panel; management
                can switch the list to every conversation (read-only unless it is theirs). */}
            {authEnabled && <ChatInboxButton canReadAll={['admin', 'owner', 'general_manager'].includes(role)} agents={agents} myAgentId={myAgentRecord?.id || null} />}
            {/* Bell — ALL sizes, eBay model (owner 2026-07-24 third ruling): icon-only in
                the app header, red count badge, pressing drops the feed panel below it
                (IntakeTab renders the panel — it owns the data — via the signal). */}
            <button type="button" aria-label={t('desk_strip_notifications')}
              className="relative w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              onClick={() => { setTab('datadesk'); setNotifSignal(s => s + 1) }}>
              <Icon name="bell" size={17} />
              {notifBadge > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-red-500 text-white text-[10px] leading-4 px-1 text-center tabular-nums font-semibold">{notifBadge}</span>
              )}
            </button>
            {authEnabled ? (
              <UserMenu name={adminName}
                myAgent={agents.find(a => a.auth_user_id && a.auth_user_id === session?.user?.id) || null} />
            ) : (
              <button onClick={() => setAdminModal(true)}
                className="text-xs text-muted-foreground hidden sm:flex items-center gap-1 hover:text-foreground border border-transparent hover:border-border rounded-lg px-2 py-1 transition-colors">
                <Icon name="user" size={13} /> {adminName}
              </button>
            )}
            {/* Desktop: individual toggles. Mobile: ONE ⚙️ menu (owner 2026-07-24) — with
                office/view-as/bell/plus already in the bar, four more buttons can't fit. */}
            <div className="hidden sm:flex items-center gap-2">
              <CurrencyToggle currency={currency} onChange={setCurrency} />
              <UsdGelRate fx={fx} />
              <LangToggle lang={lang} onChange={handleLangChange} />
              <button onClick={() => setIsDark(toggleDark())}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors text-base">
                {isDark ? '☀️' : '🌙'}
              </button>
            </div>
            <MobilePrefsMenu currency={currency} onCurrency={setCurrency}
              lang={lang} onLang={handleLangChange}
              isDark={isDark} onDark={() => setIsDark(toggleDark())} fx={fx}
              pickers={[
                ...(offices.length > 0 && canPickOffice ? [{
                  key: 'office', label: t('prefs_office'), value: officeFilter, onChange: setOfficeFilter,
                  active: officeFilter !== 'all',
                  options: [
                    { value: 'all', label: t('office_filter_all') },
                    ...offices.map(o => ({ value: o.id, label: o.name })),
                    { value: 'unassigned', label: t('office_filter_unassigned') },
                  ],
                }] : []),
                ...(isAdmin ? [{
                  key: 'viewAs', label: t('prefs_view_as'), value: viewAs || 'admin',
                  onChange: v => setViewAs(v === 'admin' ? null : v), active: !!viewAs,
                  options: [
                    { value: 'admin',           label: 'Admin' },
                    { value: 'general_manager', label: 'General Manager' },
                    { value: 'manager',         label: 'Manager' },
                    { value: 'agent',           label: 'Agent' },
                    { value: 'agent_hr',        label: 'Agent +HR' },
                  ],
                }] : []),
              ]} />
          </div>
        </div>
      </header>

      <div className="w-full px-4 sm:px-6 py-5 flex-1">
        {/* Tabs */}
        <div className="flex items-end mb-5 border-b border-border min-w-0">
          {/* Scrolls sideways inside its own row on a phone instead of widening the page. */}
          <div className="flex gap-0 flex-1 min-w-0 overflow-x-auto overflow-y-hidden no-scrollbar" style={{ scrollbarWidth: 'none' }}>
            {visibleTabs.map(tab_ => (
              /* ⚠️ On a phone the label is hidden, so the button MUST carry its name in aria-label/title (audit 2026-10-21:
                 nine unnamed icon buttons, two chart icons nobody could tell apart). The SELECTED tab keeps its label on
                 the phone too, so the reader always sees where they are. */
              <button key={tab_.id} onClick={() => leaveContractTo(tab_.id)}
                aria-label={tab_.label || t(tab_.labelKey)} title={tab_.label || t(tab_.labelKey)}
                aria-current={tab_.id === tab && !contractRoute ? 'page' : undefined}
                className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 min-h-[44px] text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                  tab_.id === tab && !contractRoute
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}>
                <Icon name={tab_.icon} size={15} /><span className={tab_.id === tab && !contractRoute ? 'inline' : 'hidden sm:inline'}>{tab_.label || t(tab_.labelKey)}</span>
              </button>
            ))}
          </div>
        </div>

        {(loading || role === null) && (
          <div className="flex items-center justify-center py-24 text-muted-foreground text-sm gap-2">
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
            </svg>
            Loading…
          </div>
        )}
        {error && !loading && (
          <div className="bg-destructive/10 border border-destructive/30 text-destructive rounded-xl px-4 py-3 text-sm flex items-center justify-between">
            <span>Error: {error}</span>
            <button onClick={reload} className="underline text-xs ml-4">{t('app_error_retry')}</button>
          </div>
        )}

        <ClockSkewBanner />
        {/* The extension download (owner 2026-09-29) — asks only when THIS browser's extension
            differs from the published build, or none answers here on a desktop. */}
        {!loading && role !== null && <ExtensionUpdateBanner />}
        {/* Shift-start prompt (T31). Mounted in the shell rather than on the attendance tab
            because an agent spends the day on the Desk — a prompt only visible where they were
            already going to look is not a notification. It renders nothing at all unless the
            person has a shift today, it has started, it has not ended, and no arrival is
            recorded; see ShiftPrompt for why there is no dismiss. */}
        {!loading && role !== null && myAgentRecord && (
          <ShiftPrompt agent={myAgentRecord} overrideMap={todayOverrides} tick={shiftTick} />
        )}
        {/* No-office alarm (owner 2026-08-01, from the irakli incident). A role that can't
            pick an office is force-scoped to `myOfficeId || 'unassigned'` above — so an
            agent whose roster row has no office sees an agency of almost nobody, and it
            SURFACES as unrelated-looking breakage (the Listings rail fell back to raw
            logins and lost its meeting markers; Schedule/Attendance go empty-ish the same
            way). Name the cause where the person who feels it will read it, because the
            person who can FIX it is an admin they have to go ask. Deliberately checks
            `myAgentRecord &&`: admins with no roster row are not misconfigured. */}
        {!loading && role !== null && !canPickOffice && myAgentRecord && !myOfficeId && (
          <div className="bg-amber-500/10 border border-amber-500/40 text-amber-700 dark:text-amber-400 rounded-xl px-4 py-3 text-sm">
            ⚠ {t('app_no_office_warning')}
          </div>
        )}
        {/* The contract page takes the body over from the tab (owner 2026-09-10, "a separate
            link"). Gated like the Desk: whoever can see the meeting card can open the page. */}
        {!loading && !error && contractRoute && can(effectiveRole, 'tab.datadesk') && (
          <TabErrorBoundary key="contract" tabId="contract">
            <Suspense fallback={<TabFallback />}><ContractPage route={contractRoute} role={role} /></Suspense>
          </TabErrorBoundary>
        )}
        {!loading && !error && tabVisible && !contractRoute && (
          <TabErrorBoundary key={tab} tabId={tab}>
            {tab === 'datadesk'   && <IntakeTab agents={filteredAgents} allAgents={agents} session={session} role={effectiveRole} offices={offices} config={config} currency={currency} onCurrency={setCurrency} fxRate={fx?.rate ?? null} deskHomeSignal={deskHomeSignal} notifSignal={notifSignal} addSignal={addSignal} onNotifBadge={setNotifBadge} shelfSignal={shelfSignal} onShelfBadges={({ fav, hidden }) => { setFavBadge(fav); setHiddenBadge(hidden) }} />}
            {tab === 'candidates' && <CandidatesTab key={lang} role={effectiveRole} me={myAgentRecord} onAgentCreated={handleAgentCreated} offices={offices} config={config} lang={lang} revertCandidateId={revertCandidateId} onRevertHandled={() => setRevertCandidateId(null)} addSignal={candidateAddSignal} onAddHandled={() => setCandidateAddSignal(0)} openCandidateId={openCandidateId} onOpenHandled={() => setOpenCandidateId(null)} />}
            {tab === 'agents'     && <AgentsTab key={lang} role={effectiveRole} roleAssignedAt={roleAssignedAt} roleHistory={roleHistory} myOfficeId={myOfficeId} agents={filteredAgents} allAgents={agents} offices={offices} officeFilterActive={officeFilter !== 'all'} onClearOfficeFilter={() => setOfficeFilter('all')} config={config} phones={phones} phoneHistory={phoneHistory} createAgent={createAgentAndPhones} editAgent={editAgentAndPhones} removeAgent={removeAgent} lang={lang} newlyHiredAgent={newlyHiredAgent} onNewlyHiredClear={(signal) => {
              setNewlyHiredAgent(null)
              if (signal?.revertCandidateId) {
                reload()
                setTab('candidates')
                setRevertCandidateId(signal.revertCandidateId)
              }
            }} />}
            {/* ONE label, two surfaces (owner 2026-09-02). An agent gets their own card and
                nothing else — "cap it only to attendance selections, no further down should
                be shown" — while management keeps the roster grid and the schedule. The
                CAPABILITY decides, not the role name, so a role gaining tab.attendance later
                needs no edit here. */}
            {tab === 'attendance' && (can(effectiveRole, 'tab.attendance')
              ? (
              <Suspense fallback={<TabFallback />}>
                <AttendanceTab key={lang} agents={filteredAgents} lang={lang} config={config} onAgentCreated={handleAgentCreated} />
                <div className="mt-8">
                  <ScheduleTab key={`sched-${lang}`} agents={filteredAgents.filter(a => a.is_active !== false)} offices={offices} lang={lang} />
                </div>
              </Suspense>
              ) : <MyAttendance agent={myAgentRecord} agents={filteredAgents} overrideMap={todayOverrides} lang={lang} />
            )}
            {tab === 'contracts'  && <Suspense fallback={<TabFallback />}><ContractsTab key={lang} agents={agents} role={effectiveRole} /></Suspense>}
            {tab === 'listings'   && <ListingsTab key={lang} agents={filteredAgents} allAgents={agents} officeFilterActive={officeFilter !== 'all'} role={effectiveRole} myAgentId={myAgentRecord?.id || null} />}
            {tab === 'deals'      && <DealsTab key={lang} agents={filteredAgents} allAgents={agents} role={role} />}
            {tab === 'stats'      && <Suspense fallback={<TabFallback />}><StatsTab key={lang} agents={filteredAgents} allAgents={agents} config={config} lang={lang} role={effectiveRole} myAgentId={myAgentRecord?.id || null} offices={offices} officeFilter={officeFilter} /></Suspense>}
            {tab === 'settings'   && <Suspense fallback={<TabFallback />}><SettingsTab key={lang} role={effectiveRole} phones={phones} agents={agents} offices={offices} onUpdatePhone={updatePhone} onAssignPhone={(agentId, number) => editAgentAndPhones(agentId, { phone_work: number })} onSaveOffice={editOffice} /></Suspense>}
            {tab === 'admin'      && <Suspense fallback={<TabFallback />}><AdminTab key={lang} role={effectiveRole} config={config} onUpdateKey={updateKey} offices={offices} onSaveOffice={editOffice} onCreateOffice={createOffice} onDeleteOffice={removeOffice} phones={phones} agents={agents} onAddPhone={addPhone} onRemovePhone={removePhone} onUpdatePhone={updatePhone} onAssignPhone={(agentId, number) => editAgentAndPhones(agentId, { phone_work: number })} adminName={adminName} onChangeAdmin={handleChangeAdmin} lang={lang} session={session} /></Suspense>}
          </TabErrorBoundary>
        )}
        {!loading && !error && role !== null && !tabVisible && !contractRoute && (
          <div className="flex items-center justify-center py-24 text-muted-foreground text-sm">
            {visibleTabs.length === 0 ? 'No views are enabled for this role yet.' : 'Select a tab.'}
          </div>
        )}
      </div>

      <footer className="border-t border-border mt-auto">
        <div className="w-full px-4 py-3 flex items-center justify-between text-xs text-muted-foreground">
          <span>Sentavus v0.5</span>
          <span>{MODE === 'local' ? t('app_offline') : t('app_connected')}</span>
        </div>
      </footer>

      {adminModal && <AdminModal onClose={handleAdminClose} />}
    </div>
  )
}
